import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import { additionalOptions, locations } from '../../../services/api';
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes, FaSync, FaMapMarkerAlt, FaGlobeEurope, FaCog, FaInfoCircle, FaExclamationCircle } from 'react-icons/fa';

// TabPanel component (improved for Tailwind)
function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`rental-options-tabpanel-${index}`}
      aria-labelledby={`rental-options-tab-${index}`}
      className={value === index ? "opacity-100 transition-opacity duration-150" : "opacity-0 hidden"}
    >
      {value === index && (
        <div className="p-6">{children}</div>
      )}
    </div>
  );
}

// Empty state component for consistent empty state display
const EmptyState = ({ title, message, icon, actionLabel, onAction, loading }) => {
  const Icon = icon || FaInfoCircle;
  
  return (
    <div className="text-center py-12 px-4 border border-dashed border-gray-300 rounded-lg bg-gray-50">
      <Icon className="mx-auto h-12 w-12 text-gray-400" />
      <h3 className="mt-2 text-xl font-medium text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-500">{message}</p>
      {actionLabel && onAction && (
        <div className="mt-6">
          <button
            type="button"
            disabled={loading}
            onClick={onAction}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <FaPlus className="-ml-1 mr-2 h-4 w-4" />
            {actionLabel}
          </button>
        </div>
      )}
    </div>
  );
};

// Loading indicator component
const LoadingIndicator = () => (
  <div className="flex justify-center items-center py-8">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    <span className="ml-3 text-gray-600 font-medium">Loading...</span>
  </div>
);

const RentalOptions = () => {
  const [tabValue, setTabValue] = useState(0);

  // Enhanced tab change handler with smooth transitions
  const handleTabChange = (newValue) => {
    setTabValue(newValue);
  };

  return (
    <div className="w-full bg-white shadow-sm rounded-lg">
      <div className="border-b border-gray-200">
        <div className="px-6 flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => handleTabChange(0)}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-all duration-200 ease-in-out ${
              tabValue === 0
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            aria-controls="rental-options-tabpanel-0"
          >
            <div className="flex items-center">
              <FaCog className="mr-2" /> 
              Additional Options
            </div>
          </button>
          <button
            onClick={() => handleTabChange(1)}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-all duration-200 ease-in-out ${
              tabValue === 1
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            aria-controls="rental-options-tabpanel-1"
          >
            <div className="flex items-center">
              <FaMapMarkerAlt className="mr-2" /> 
              Locations
            </div>
          </button>
        </div>
      </div>

      <TabPanel value={tabValue} index={0}>
        <AdditionalOptionsTab />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <LocationsTab />
      </TabPanel>
    </div>
  );
};

// Additional Options Tab Component (Enhanced with improved UX)
const AdditionalOptionsTab = () => {
  const [optionsList, setOptionsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editOption, setEditOption] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    isEnabled: true
  });
  const [errors, setErrors] = useState({});
  const [initialLoad, setInitialLoad] = useState(true);

  const fetchOptions = useCallback(async () => {
    if (!loading) {
      try {
        const response = await additionalOptions.getAll();
        console.log('Fetched options:', response);
        setOptionsList(response || []);
      } catch (error) {
        console.error('Error fetching additional options:', error);
        Swal.fire({
          title: 'Error',
          text: 'Error loading additional options',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    }
  }, [loading]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const handleOpenDialog = (option = null) => {
    if (option) {
      setEditOption(option);
      setFormData({
        name: option.name,
        description: option.description || '',
        price: option.price,
        isEnabled: option.isEnabled
      });
    } else {
      setEditOption(null);
      setFormData({
        name: '',
        description: '',
        price: 0,
        isEnabled: true
      });
    }
    setErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (formData.price < 0) newErrors.price = 'Price cannot be negative';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      if (editOption) {
        await additionalOptions.update(editOption._id, formData);
        Swal.fire({
          title: 'Success',
          text: 'Option updated successfully',
          icon: 'success',
          confirmButtonColor: '#3085d6',
          timer: 1500
        });
      } else {
        await additionalOptions.create(formData);
        Swal.fire({
          title: 'Success',
          text: 'Option created successfully',
          icon: 'success',
          confirmButtonColor: '#3085d6',
          timer: 1500
        });
      }
      handleCloseDialog();
      fetchOptions();
    } catch (error) {
      console.error('Error saving option:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Error saving option',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (option) => {
    Swal.fire({
      title: `${option.isEnabled ? 'Disable' : 'Enable'} Option`,
      text: `Are you sure you want to ${option.isEnabled ? 'disable' : 'enable'} "${option.name}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: option.isEnabled ? '#d33' : '#3085d6',
      cancelButtonColor: '#6c757d',
      confirmButtonText: option.isEnabled ? 'Yes, disable it' : 'Yes, enable it'
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const updatedOption = { ...option, isEnabled: !option.isEnabled };
          await additionalOptions.update(option._id, updatedOption);
          await fetchOptions();
          Swal.fire({
            title: 'Success',
            text: `Option ${updatedOption.isEnabled ? 'enabled' : 'disabled'} successfully`,
            icon: 'success',
            confirmButtonColor: '#3085d6',
            timer: 1500
          });
        } catch (error) {
          console.error('Error toggling option status:', error);
          Swal.fire({
            title: 'Error',
            text: 'Error updating option status',
            icon: 'error',
            confirmButtonColor: '#3085d6'
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };
  
  const handleDeleteOption = (option) => {
    Swal.fire({
      title: 'Delete Option',
      text: `Are you sure you want to delete "${option.name}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it'
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          await additionalOptions.delete(option._id);
          await fetchOptions();
          Swal.fire({
            title: 'Deleted!',
            text: 'Option has been deleted.',
            icon: 'success',
            confirmButtonColor: '#3085d6',
            timer: 1500
          });
        } catch (error) {
          console.error('Error deleting option:', error);
          Swal.fire({
            title: 'Error',
            text: 'Error deleting option',
            icon: 'error',
            confirmButtonColor: '#3085d6'
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Manage Additional Options</h2>
        <button
          onClick={() => handleOpenDialog()}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md inline-flex items-center disabled:opacity-50 transition-colors duration-200"
        >
          <FaPlus className="mr-2" /> Add Option
        </button>
      </div>

      {loading && initialLoad ? (
        <LoadingIndicator />
      ) : (
        <>
          {(!optionsList || optionsList.length === 0) ? (
            <EmptyState 
              title="No additional options found" 
              message="Add rental options like GPS, baby seats, or additional insurance to offer to your customers."
              icon={FaExclamationCircle}
              actionLabel="Add Your First Option"
              onAction={() => handleOpenDialog()}
              loading={loading}
            />
          ) : (
            <div className="bg-white overflow-hidden shadow-sm rounded-lg">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {optionsList.map(option => (
                      <tr key={option._id} className={!option.isEnabled ? 'bg-gray-50 opacity-70' : 'hover:bg-gray-50 transition-colors duration-150'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{option.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-md truncate">{option.description}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {option.price === 0 ? 'Free' : `€${Number(option.price).toFixed(2)}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            option.isEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {option.isEnabled ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button 
                            onClick={() => handleOpenDialog(option)} 
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button 
                            onClick={() => handleToggleStatus(option)} 
                            className={`p-1 rounded ${
                              option.isEnabled 
                              ? 'text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50' 
                              : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                            }`}
                            title={option.isEnabled ? 'Disable' : 'Enable'}
                          >
                            {option.isEnabled ? <FaTimes /> : <FaCheck />}
                          </button>
                          <button 
                            onClick={() => handleDeleteOption(option)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal */}
      {openDialog && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FaCog className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      {editOption ? 'Edit Option' : 'Add New Option'}
                    </h3>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="name"
                          id="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={`mt-1 block w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                          placeholder="e.g. GPS, Child Seat, Insurance"
                        />
                        {errors.name && <p className="mt-2 text-sm text-red-600">{errors.name}</p>}
                      </div>
                      <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea
                          name="description"
                          id="description"
                          rows="3"
                          value={formData.description}
                          onChange={handleInputChange}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          placeholder="Provide a short description of this option"
                        ></textarea>
                      </div>
                      <div>
                        <label htmlFor="price" className="block text-sm font-medium text-gray-700">Price (€)</label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">€</span>
                          </div>
                          <input
                            type="number"
                            name="price"
                            id="price"
                            value={formData.price}
                            onChange={handleInputChange}
                            className={`pl-7 block w-full px-3 py-2 border ${errors.price ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                          />
                        </div>
                        {errors.price && <p className="mt-2 text-sm text-red-600">{errors.price}</p>}
                        <p className="mt-1 text-xs text-gray-500">Set to 0 for free options.</p>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="isEnabled"
                          name="isEnabled"
                          type="checkbox"
                          checked={formData.isEnabled}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <label htmlFor="isEnabled" className="ml-2 block text-sm text-gray-900">
                          Enabled
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  disabled={loading}
                  onClick={handleSubmit}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseDialog}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Locations Tab Component (Enhanced UX)
const LocationsTab = () => {
  const [locationsList, setLocationsList] = useState([]);
  const [popularLocations, setPopularLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editLocation, setEditLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    region: '',
    country: 'Greece', // Default country
    isEnabled: true
  });
  const [errors, setErrors] = useState({});
  const [regionFilter, setRegionFilter] = useState('all');
  const [regions, setRegions] = useState([]);
  const [showPopular, setShowPopular] = useState(false);
  const [recalculatingPopularity, setRecalculatingPopularity] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const fetchLocations = useCallback(async () => {
    if (!loading) {
      try {
        const response = await locations.getAll();
        const locationsData = response || [];
        setLocationsList(locationsData);
        
        // Extract unique regions
        const uniqueRegions = [...new Set(locationsData.map(loc => loc.region).filter(Boolean))].sort();
        setRegions(uniqueRegions);
        
        // Auto-select first region if there are locations but no region is selected
        if (uniqueRegions.length > 0 && regionFilter === 'all' && locationsData.length > 0) {
          // Keep 'all' selected by default, but this is where to change it if desired
          // setRegionFilter(uniqueRegions[0]);
        }
      } catch (error) {
        console.error('Error fetching locations:', error);
        Swal.fire({
          title: 'Error',
          text: 'Error loading locations',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    }
  }, [loading, regionFilter]);

  const fetchPopularLocations = useCallback(async () => {
    try {
      // This would be an API call in a real scenario
      // For now, simulate by taking top 5 based on any existing popularity field
      const sortedLocations = [...locationsList]
        .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
        .slice(0, 5);
      
      setPopularLocations(sortedLocations);
      setShowPopular(sortedLocations.length > 0);
    } catch (error) {
      console.error('Error sorting popular locations:', error);
    }
  }, [locationsList]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  useEffect(() => {
    if (locationsList.length > 0) {
      fetchPopularLocations();
    }
  }, [locationsList, fetchPopularLocations]);

  const handleOpenDialog = (location = null) => {
    if (location) {
      setEditLocation(location);
      setFormData({
        name: location.name,
        region: location.region || '',
        country: location.country || 'Greece',
        isEnabled: location.isEnabled
      });
    } else {
      setEditLocation(null);
      setFormData({
        name: '',
        region: regions.length > 0 ? regions[0] : '',
        country: 'Greece',
        isEnabled: true
      });
    }
    setErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.region.trim()) newErrors.region = 'Region is required';
    if (!formData.country.trim()) newErrors.country = 'Country is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      if (editLocation) {
        await locations.update(editLocation._id, formData);
        Swal.fire({
          title: 'Success',
          text: 'Location updated successfully',
          icon: 'success',
          confirmButtonColor: '#3085d6',
          timer: 1500
        });
      } else {
        await locations.create(formData);
        Swal.fire({
          title: 'Success',
          text: 'Location created successfully',
          icon: 'success',
          confirmButtonColor: '#3085d6',
          timer: 1500
        });
      }
      handleCloseDialog();
      await fetchLocations(); // Refetch all locations to update list and regions
    } catch (error) {
      console.error('Error saving location:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Error saving location',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (location) => {
     Swal.fire({
      title: `${location.isEnabled ? 'Disable' : 'Enable'} Location`,
      text: `Are you sure you want to ${location.isEnabled ? 'disable' : 'enable'} "${location.name}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: location.isEnabled ? '#d33' : '#3085d6',
      cancelButtonColor: '#6c757d',
      confirmButtonText: location.isEnabled ? 'Yes, disable it' : 'Yes, enable it'
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const updatedLocation = { ...location, isEnabled: !location.isEnabled };
          await locations.update(location._id, updatedLocation);
          await fetchLocations();
          Swal.fire({
            title: 'Success',
            text: `Location ${updatedLocation.isEnabled ? 'enabled' : 'disabled'} successfully`,
            icon: 'success',
            confirmButtonColor: '#3085d6',
            timer: 1500
          });
        } catch (error) {
          console.error('Error toggling location status:', error);
          Swal.fire({
            title: 'Error',
            text: 'Error updating location status',
            icon: 'error',
            confirmButtonColor: '#3085d6'
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleDeleteLocation = (location) => {
    Swal.fire({
      title: 'Delete Location',
      text: `Are you sure you want to delete "${location.name}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it'
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          await locations.delete(location._id);
          await fetchLocations(); // Refetch to update list and regions
          Swal.fire({
            title: 'Deleted!',
            text: 'Location has been deleted.',
            icon: 'success',
            confirmButtonColor: '#3085d6',
            timer: 1500
          });
        } catch (error) {
          console.error('Error deleting location:', error);
          Swal.fire({
            title: 'Error',
            text: 'Error deleting location',
            icon: 'error',
            confirmButtonColor: '#3085d6'
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };
  
  const handleRecalculatePopularity = async () => {
    setRecalculatingPopularity(true);
    try {
      // For now, this is a simulated action
      // In a real implementation, there would be a backend call
      
      // Simulate recalculation with a delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      Swal.fire({
        title: 'Success',
        text: 'Location popularity recalculated successfully',
        icon: 'success',
        confirmButtonColor: '#3085d6',
        timer: 1500
      });
      
      // Refresh with new data
      await fetchLocations();
      fetchPopularLocations();
    } catch (error) {
      console.error('Error recalculating popularity:', error);
      Swal.fire({
        title: 'Error',
        text: 'Error recalculating popularity',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setRecalculatingPopularity(false);
    }
  };

  const filteredLocations = locationsList 
    ? locationsList.filter(location => 
        regionFilter === 'all' || location.region === regionFilter
      )
    : [];

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h2 className="text-2xl font-semibold text-gray-800">Manage Locations</h2>
        <div className="flex flex-wrap sm:flex-nowrap gap-2 items-center">
          {regions.length > 0 && (
            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="block w-full sm:w-auto px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="all">All Regions</option>
              {regions.map(region => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => handleOpenDialog()}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md inline-flex items-center disabled:opacity-50 transition-colors duration-200 w-full sm:w-auto justify-center"
          >
            <FaPlus className="mr-2" /> Add Location
          </button>
        </div>
      </div>
      
      {/* Popular Locations Section - Enhanced */}
      {showPopular && popularLocations.length > 0 && (
        <div className="mb-6 bg-white shadow-sm rounded-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-700">Popular Locations</h3>
            <button 
              onClick={handleRecalculatePopularity}
              disabled={recalculatingPopularity}
              className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center disabled:opacity-50 px-2 py-1 rounded hover:bg-blue-50 transition-colors duration-150"
            >
              <FaSync className={`mr-1 ${recalculatingPopularity ? 'animate-spin' : ''}`} /> Recalculate
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {popularLocations.map(location => (
              <div key={location._id} className="border rounded-lg p-3 hover:shadow-md transition-shadow duration-200">
                <div className="text-lg font-medium text-gray-900 truncate" title={location.name}>{location.name}</div>
                <div className="text-sm text-gray-500">{location.region}, {location.country}</div>
                <div className="flex items-center mt-2 text-sm text-blue-600">
                  <FaMapMarkerAlt className="mr-1" />
                  <span>Used {location.popularity || 0} times</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && initialLoad ? (
        <LoadingIndicator />
      ) : (
        <>
          {(!locationsList || locationsList.length === 0) ? (
            <EmptyState 
              title="No locations found" 
              message="Add pickup and drop-off locations for your rental services."
              icon={FaMapMarkerAlt}
              actionLabel="Add Your First Location"
              onAction={() => handleOpenDialog()}
              loading={loading}
            />
          ) : filteredLocations.length === 0 ? (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <FaExclamationCircle className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    No locations found in this region. Try selecting a different region or add a new location.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white overflow-hidden shadow-sm rounded-lg">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Region</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Popularity</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredLocations.map(location => (
                      <tr key={location._id} className={!location.isEnabled ? 'bg-gray-50 opacity-70' : 'hover:bg-gray-50 transition-colors duration-150'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{location.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{location.region}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{location.country}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{location.popularity || 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            location.isEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {location.isEnabled ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button 
                            onClick={() => handleOpenDialog(location)} 
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button 
                            onClick={() => handleToggleStatus(location)} 
                            className={`p-1 rounded ${
                              location.isEnabled 
                              ? 'text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50' 
                              : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                            }`}
                            title={location.isEnabled ? 'Disable' : 'Enable'}
                          >
                            {location.isEnabled ? <FaTimes /> : <FaCheck />}
                          </button>
                          <button 
                            onClick={() => handleDeleteLocation(location)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal for Locations */}
      {openDialog && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FaMapMarkerAlt className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title-location">
                      {editLocation ? 'Edit Location' : 'Add New Location'}
                    </h3>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label htmlFor="location-name" className="block text-sm font-medium text-gray-700">Name <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="name"
                          id="location-name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={`mt-1 block w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                          placeholder="e.g. Athens Airport, Syntagma Square"
                        />
                        {errors.name && <p className="mt-2 text-sm text-red-600">{errors.name}</p>}
                      </div>
                      <div>
                        <label htmlFor="region" className="block text-sm font-medium text-gray-700">Region <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="region"
                          id="region"
                          list="regionsList"
                          value={formData.region}
                          onChange={handleInputChange}
                          className={`mt-1 block w-full px-3 py-2 border ${errors.region ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                          placeholder="e.g. Attica, Crete, Santorini"
                        />
                        {regions.length > 0 && (
                          <datalist id="regionsList">
                            {regions.map(region => (
                              <option key={region} value={region} />
                            ))}
                          </datalist>
                        )}
                        {errors.region && <p className="mt-2 text-sm text-red-600">{errors.region}</p>}
                      </div>
                      <div>
                        <label htmlFor="country" className="block text-sm font-medium text-gray-700">Country <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="country"
                          id="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          className={`mt-1 block w-full px-3 py-2 border ${errors.country ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                          placeholder="e.g. Greece"
                        />
                        {errors.country && <p className="mt-2 text-sm text-red-600">{errors.country}</p>}
                      </div>
                      <div className="flex items-center mt-4">
                        <input
                          id="location-isEnabled"
                          name="isEnabled"
                          type="checkbox"
                          checked={formData.isEnabled}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <label htmlFor="location-isEnabled" className="ml-2 block text-sm text-gray-900">
                          Enabled
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  disabled={loading}
                  onClick={handleSubmit}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseDialog}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RentalOptions;